import { useRef, ChangeEvent } from 'react';

interface Props {
  label?: string;
  value: string;
  onChange: (val: string) => void;
  className?: string;
}

const compressImage = (file: File, maxWidth = 900): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });

const ImageUploadField = ({ label, value, onChange, className = '' }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    onChange(compressed);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
          {label}
        </label>
      )}
      <div className="flex gap-2 items-start">
        <div className="flex-1 space-y-1.5">
          <div className="flex gap-1.5">
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="URL de imagen o sube una foto..."
              className="flex-1 min-w-0 bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              title="Seleccionar imagen del dispositivo"
              className="flex items-center gap-1 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 hover:text-white text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors border border-zinc-600"
            >
              <i className="ri-upload-2-line text-sm" />
              <span className="hidden sm:inline">Examinar</span>
            </button>
          </div>
          {value && (
            <div className="relative inline-block">
              <img
                src={value}
                alt="preview"
                className="h-20 w-32 object-cover object-top border border-zinc-700"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-black/80 text-white hover:bg-red-600 transition-colors cursor-pointer text-xs"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
};

export default ImageUploadField;
